/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo } from 'react';

// Compiled isn't ready to be used in components used by Ecosystem
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css, jsx } from '@emotion/react';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { formatValue } from './internal/utils';
var boxStyles = xcss({
  borderRadius: 'border.radius.200',
  display: 'inline-flex',
  blockSize: 'min-content',
  flexShrink: 0 // Text component can wrap text, this ensures it doesn't wrap in flex containers.
});

/**
 * Visual refresh colors.
 * Hardcoded hex colors are used as the they should not change based on theme, and there are no appropriate color tokens.
 *
 * Using separate variables as opposed to an object, to comply with UI styling standard
 * https://atlassian.design/components/eslint-plugin-ui-styling-standard/no-unsafe-values/usage#object-access
 */
var neutral300 = '#DDDEE1';
var red300 = '#FD9891';
var blue300 = '#8FB8F6';
var neutral1000 = '#292A2E';
var styles = {
  root: css({
    display: 'inline-flex',
    boxSizing: 'border-box',
    minWidth: "var(--ds-space-300, 24px)",
    justifyContent: 'center',
    flexShrink: 0,
    blockSize: 'min-content',
    borderRadius: "var(--ds-border-radius-050, 2px)",
    paddingInline: "var(--ds-space-050, 4px)"
  }),
  added: css({
    backgroundColor: "var(--ds-background-success, #DCFFF1)",
    color: "var(--ds-text, #172B4D)"
  }),
  default: css({
    backgroundColor: neutral300,
    color: neutral1000
  }),
  important: css({
    backgroundColor: red300,
    color: neutral1000
  }),
  primary: css({
    backgroundColor: blue300,
    color: neutral1000
  }),
  primaryInverted: css({
    backgroundColor: "var(--ds-surface, #FFFFFF)",
    color: "var(--ds-text-brand, #0C66E4)"
  }),
  removed: css({
    backgroundColor: "var(--ds-background-danger, #FFECEB)",
    color: "var(--ds-text, #172B4D)"
  })
};

/**
 * __Badge__
 *
 * This component gives you the full badge functionality and automatically formats the number you provide in \`children\`.
 *
 * - [Examples](https://atlassian.design/components/badge/examples)
 * - [Code](https://atlassian.design/components/badge/code)
 * - [Usage](https://atlassian.design/components/badge/usage)
 */
var Badge = /*#__PURE__*/memo(function Badge(_ref) {
  var _ref$appearance = _ref.appearance,
    appearance = _ref$appearance === void 0 ? 'default' : _ref$appearance,
    _ref$children = _ref.children,
    children = _ref$children === void 0 ? 0 : _ref$children,
    _ref$max = _ref.max,
    max = _ref$max === void 0 ? 99 : _ref$max,
    style = _ref.style,
    testId = _ref.testId;
  if (fg('platform-component-visual-refresh')) {
    return jsx("span", {
      "data-testid": testId,
      css: [styles.root, styles[appearance]],
      style: {
        background: style === null || style === void 0 ? void 0 : style.backgroundColor,
        color: style === null || style === void 0 ? void 0 : style.color
      }
    }, jsx(Text, {
      size: "small",
      align: "center",
      color: "inherit"
    }, typeof children === 'number' && max ? formatValue(children, max) : children));
  }
  return jsx(Box, {
    testId: testId,
    as: "span",
    backgroundColor: backgroundColors[appearance],
    xcss: boxStyles,
    style: {
      background: style === null || style === void 0 ? void 0 : style.backgroundColor,
      color: style === null || style === void 0 ? void 0 : style.color
    },
    paddingInline: "space.075"
  }, jsx(Text, {
    size: "UNSAFE_small",
    align: "center",
    color: style !== null && style !== void 0 && style.color ? 'inherit' : textColors[appearance]
  }, typeof children === 'number' && max ? formatValue(children, max) : children));
});
Badge.displayName = 'Badge';
export default Badge;
var backgroundColors = {
  added: 'color.background.success',
  default: 'color.background.neutral',
  important: 'color.background.danger.bold',
  primary: 'color.background.brand.bold',
  primaryInverted: 'elevation.surface',
  removed: 'color.background.danger'
};
var textColors = {
  added: 'color.text.success',
  default: 'color.text',
  important: 'color.text.inverse',
  primary: 'color.text.inverse',
  primaryInverted: 'color.text.brand',
  removed: 'color.text.danger'
};